import React, {useEffect, useState} from 'react';
import useGlobalUIStore from '@/stores';
import {useLocation, useNavigate} from 'react-router-dom';
import {convertToArray, getLocationForTagClickLog} from '@/utils';
import {getSearchInflowTab, getUrlLocation} from '@/utils/main/search';
import LogApi from '@/api/common/log';
import {useBroadcastContext} from '@/components/main/common/Broadcast/BroadcastProvider';

/** @deprecated utils/helper.js에 정의된 함수로 사용해주세요. */
export const generateStyle = (tag, data, theme, type) => {
  try {
    if (tag && data) {
      const element = data?.filter((val) => tag === val.tagName);

      if (element.length > 0) {
        const {event, style} = element[0][theme].pc || {};
        if (type === 'style') {
          return style;
        } else {
          return event[type];
        }
      }
    }
    return undefined;
  } catch (error) {
    return undefined;
  }
};

/**
 * @typedef {Object} ChipCategoryParams
 * @property {string} location lnb 메뉴 접근 경로 (sck2(stopword)에 쏘는 location 로그 변수 MY+, HOT, ALL : main, VOD : vod_main, MY : my_main)
 * @property {string} hashType 현재 태그 타입 LIVE, VOD - 방송 태그 or vod 태그
 * @property {string[]} autoHashTags 자동 해시태그 목록 (이벤트성 태그가 있을 경우 이쪽으로 내려 주는 걸로 협의)
 * @property {boolean} hasDrops 드롭스 여부
 * @property {string[]} categoryTags 카테고리 태그 목록 (방송 설정할 때)
 * @property {string} categoryName 카테고리 이름
 * @property {string[]} hashTags 해시태그 목록 (유저가 설정한 태그)
 * @property {string} searchArea 페이지 내 어떤 영역에서 검색했는지 ( SCK2 로그 관련 )
 */

/**
 * 전역에서 사용하고 있는 tag chip
 *
 * @desc 태그 노출 순서는 1. autoHashtags => 2. hasDrops => 3. categoryTags => 4. hashTags
 *
 * @param {ChipCategoryParams} params
 * @returns {string[]} tag-wrap 하위의 아이템
 * */
const ChipCategory = ({
  categoryName,
  hashType,
  autoHashTags: eventTags = [],
  hasDrops = false,
  categoryTags = [],
  hashTags = [],
  searchArea = 'total',
  onClickLog,
}) => {
  const {broadcastType} = useBroadcastContext();
  const location = useLocation(); // props로 전달 받고 있어서 새로 선언
  const navigate = useNavigate();
  const {autoTagStyle} = useGlobalUIStore();
  const [theme, setTheme] = useState(
    document.documentElement.getAttribute('dark') ? 'dark' : 'light',
  );
  const handleMoveTag = (event, tagName, hashtype, type = '') => {
    event.preventDefault();
    const urlLocation = getUrlLocation(location.pathname);
    const _hashType = hashtype.toLowerCase();
    const inflowTab = getSearchInflowTab(location.pathname);

    const query = {
      hash: 'hashtag',
      tagname: tagName,
      hashtype: _hashType,
      stype: 'hash',
      acttype: searchArea,
      location: urlLocation,
      inflow_tab: inflowTab,
    };

    let path = '';
    if (type === 'category') {
      const encodeCategoryName = encodeURIComponent(categoryName || tagName);
      //탐색 상세페이지로 이동(방송 태그에 한에서만)
      path = `/directory/category/${encodeCategoryName}/${hashType.toLowerCase()}`;
    } else {
      const queryString = new URLSearchParams(query).toString();
      path = `/search?${queryString}`;
    }

    // sck2 로그 관련 태그 검색 페이지 뒤로가기 처리
    // 목적: 태그 검색 페이지에서 뒤로가기 시 sck2로그에 올바른 히스토리 상태 유지
    // 1. 현재 페이지가 '/search'로 시작하는 경우에만 실행
    if (location.pathname.startsWith('/search') && type !== 'category') {
      // 2. 현재 URL의 파라미터 추출
      const currentParams = new URLSearchParams(location.search);

      // 3. URL 파라미터 수정
      // - 'location' 파라미터가 있으면 값을 'hash'로 설정
      // - 없으면 'szLocation' 파라미터를 'hash'로 설정
      if (currentParams.has('location')) {
        currentParams.set('location', 'hash');
      } else {
        currentParams.set('szLocation', 'hash');
      }

      // 4. 수정된 URL을 history에 push
      // replace: true 옵션으로 현재 history 항목을 대체
      const desiredPath = `${location.pathname}?${currentParams.toString()}`;
      navigate(desiredPath, {replace: true});
    }

    onClickLog && onClickLog(type === 'drops' ? 'drops' : 'hash');

    // 실제 페이지 이동을 수행합니다.
    navigate(path);
  };

  /**
   * 이벤트 & 드롭스 태그 클릭 시 로그를 발송하는 함수
   */
  const sendEventTagClickLog = (buttonType) => {
    LogApi.sendClickLog({
      codeType: 'tags',
      sendData: {
        location: getLocationForTagClickLog(location.pathname, location.search),
        contentsType: broadcastType,
        buttonType: buttonType,
      },
    });
  };

  const setAutoTagStyleObserver = () => {
    try {
      const $container = document.querySelector('html');
      const handleDarkModeChange = () => {
        const newMode = document.documentElement.getAttribute('dark');
        setTheme(newMode ? 'dark' : 'light');
      };

      const observer = new MutationObserver(handleDarkModeChange);
      observer.observe($container, {
        attributes: true,
        attributeFilter: ['dark'],
      });

      return () => {
        observer.disconnect();
      };
    } catch (error) {}
  };

  useEffect(() => {
    setAutoTagStyleObserver();
  }, []);

  return (
    <div data-testid='chips' className='tag_wrap'>
      {/* 이벤트 태그 */}
      {convertToArray(eventTags).map(
        (name, idx) =>
          name &&
          name.length > 0 && (
            <a
              key={`event_tag_${idx}`}
              href='#'
              onClick={(event) => {
                handleMoveTag(event, name, hashType);
                // sendEventTagClickLog(); // TODO. 드롭스 태그에 사용된 태그 클릭 시 로그를 추후 이벤트 태그에도 사용 (기획 예정)
              }}
              style={generateStyle(name, autoTagStyle, theme, 'style')}
              onMouseEnter={(e) => {
                const styleData = generateStyle(
                  name,
                  autoTagStyle,
                  theme,
                  'hover',
                );
                if (styleData) {
                  const {background, color} = styleData;
                  e.currentTarget.style.background = background;
                  e.currentTarget.style.color = color;
                }
              }}
              onMouseLeave={(e) => {
                const styleData = generateStyle(
                  name,
                  autoTagStyle,
                  theme,
                  'style',
                );
                if (styleData) {
                  const {background, color} = styleData;
                  e.currentTarget.style.background = background;
                  e.currentTarget.style.color = color;
                }
              }}>
              {name}
            </a>
          ),
      )}
      {/* Note.
       * 신규 이벤트성 태그의 경우 서버의 응답 event_auto_hashtags.php로부터 스타일을 부여받는 것이 설계 의도.
       * 만약 신규 태그에 대한 기능 개발이 시작된다면 아래 처럼 분기 처리하지 않고
       * event_auto_hashtags를 사용 유지하는 방향을 우선으로 할 것.
       * (드롭스 태그의 경우 사불가론에 의해 별도 분리 유지되었음)
       **/}
      {/* 드롭스 태그 */}
      {!!Number(hasDrops) && (
        <a
          className='drops'
          href='#'
          onClick={(event) => {
            handleMoveTag(event, '드롭스', 'LIVE', 'drops');
            sendEventTagClickLog('drops_tags');
          }}>
          <span>드롭스</span>
        </a>
      )}
      {/* 카테고리 태그 */}
      {convertToArray(categoryTags).map(
        (name, idx) =>
          name &&
          name.length > 0 && (
            <a
              key={`category_tag_${idx}`}
              className='category'
              href='#'
              onClick={(event) => {
                handleMoveTag(event, name, hashType, 'category');
              }}>
              {name}
            </a>
          ),
      )}
      {/* 유저 태그 */}
      {convertToArray(hashTags).map(
        (name, idx) =>
          name &&
          name.length > 0 && (
            <a
              key={`user_hash_tag_${idx}`}
              href='#'
              onClick={(event) => {
                handleMoveTag(event, name, hashType);
              }}>
              {name}
            </a>
          ),
      )}
    </div>
  );
};

/**
 * 전역에서 사용하고 있는 tag chip
 *
 * @param {ChipCategoryParams} params
 * @returns {string[]} tag-wrap 하위의 아이템
 * */
export default React.memo(ChipCategory);
